/**
 * API Client with JWT authentication and middleware support
 */

import { normalizeEnvelopeErrorDetails } from '@ai-content-hub/shared-api';

import { getApiAcceptLanguage } from '@/i18n';
import { useSelectedTenantStore } from '@/store/selectedTenantStore';
import { useAuthStore } from '@/store/authStore';
import { authService } from '../auth.service';
import {
    clearEmbedApiToken,
    getEmbedApiToken,
    getEmbedBearerToken,
} from '@/utils/embedApiToken';
import {
    isEmbedParentMessagingActive,
    notifyEmbedParentApiAction,
} from '@/utils/embedParentMessaging';
import { parseTenantRenameInfo, useTenantRenameStore } from '@/store/tenantRenameStore';

/** ACH's business code for "this cơ sở is mid-rename" (envelope `message`). */
const TENANT_RENAME_ERROR_CODE = 'ERROR_TENANT_SLUG_RENAME_IN_PROGRESS';

export interface ApiConfig {
    baseUrl: string;
    timeout?: number;
}

export interface RequestConfig extends RequestInit {
    timeout?: number;
    skipAuth?: boolean; // Skip adding auth header (for login/refresh)
    _retry?: boolean; // Internal flag to prevent infinite retry loops
}

export interface ApiResponse<T = unknown> {
    data: T;
    status: number;
    ok: boolean;
}

/** Envelope returned by the backend for all API responses. */
export interface ApiEnvelope<T = unknown> {
    success: boolean;
    code: number;
    message: string;
    description: string;
    data: T;
    /** Normalized field errors (strings only); present when code is validation (e.g. 2000). */
    details?: Record<string, string[]>;
}

export interface ApiError {
    message: string;
    description?: string;
    status?: number;
    code?: string;
    errorCode?: number;
    /** Field-level validation messages from envelope `details` (e.g. code 2000). */
    details?: Record<string, string[]>;
}

type RequestMiddleware = (config: RequestConfig) => RequestConfig | Promise<RequestConfig>;
type ResponseMiddleware = (response: Response) => Response | Promise<Response>;
type ErrorMiddleware = (error: ApiError) => ApiError | Promise<ApiError>;

class ApiClient {
    private baseUrl: string;
    private timeout: number;
    private requestMiddlewares: RequestMiddleware[] = [];
    private responseMiddlewares: ResponseMiddleware[] = [];
    private errorMiddlewares: ErrorMiddleware[] = [];

    constructor(config: ApiConfig) {
        this.baseUrl = config.baseUrl;
        this.timeout = config.timeout || 30000;
    }

    // Middleware registration
    useRequestMiddleware(middleware: RequestMiddleware): this {
        this.requestMiddlewares.push(middleware);
        return this;
    }

    useResponseMiddleware(middleware: ResponseMiddleware): this {
        this.responseMiddlewares.push(middleware);
        return this;
    }

    useErrorMiddleware(middleware: ErrorMiddleware): this {
        this.errorMiddlewares.push(middleware);
        return this;
    }

    // Apply middlewares
    private async applyRequestMiddlewares(config: RequestConfig): Promise<RequestConfig> {
        let result = config;
        for (const middleware of this.requestMiddlewares) {
            result = await middleware(result);
        }
        return result;
    }

    private async applyResponseMiddlewares(response: Response): Promise<Response> {
        let result = response;
        for (const middleware of this.responseMiddlewares) {
            result = await middleware(result);
        }
        return result;
    }

    private async applyErrorMiddlewares(error: ApiError): Promise<ApiError> {
        let result = error;
        for (const middleware of this.errorMiddlewares) {
            result = await middleware(result);
        }
        return result;
    }

    // Default headers with JWT token
    private getDefaultHeaders(skipAuth = false): HeadersInit {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        // Attach app version for backend logging
        if (window.APP_VERSION) {
            headers['X-App-Version'] = window.APP_VERSION;
        }

        // Attach content-type package versions
        if (window.CONTENT_TYPE_VERSIONS) {
            headers['X-Content-Type-Versions'] = JSON.stringify(window.CONTENT_TYPE_VERSIONS);
        }

        if (!skipAuth) {
            const embedBearer = getEmbedBearerToken();
            if (embedBearer) {
                headers['Authorization'] = `Bearer ${embedBearer}`;
            } else {
                const embedToken = getEmbedApiToken();
                if (embedToken) {
                    headers['Authorization'] = `Token ${embedToken}`;
                } else {
                    const token = authService.getAccessToken();
                    if (token) {
                        headers['Authorization'] = `Bearer ${token}`;
                    }
                }
            }
        }

        headers['Accept-Language'] = getApiAcceptLanguage();

        /*
         * EAA-1817: the acting ACH tenant.
         *
         * `OIDCAuthentication._resolve_acting_tenant` picks the membership named by
         * this header; an identity with SEVERAL memberships and no header resolves
         * to NO acting tenant and an empty permission set, because the server
         * refuses to guess between them. web-content never sent it, so a
         * multi-tenant member could only ever act as whichever single membership the
         * server could infer — and switching tenants changed nothing server-side.
         *
         * Read per request, not captured once: the selection changes while the app
         * runs.
         *
         * NOT sent for platform staff, deliberately. EAA-1886 found that giving staff
         * an acting tenant re-pins `request.tenant`, and every console view resolves
         * its scope as "`request.tenant`, else `?tenant_id=` for staff" — so the
         * query param is never reached and the staff switcher silently stops working.
         * Staff scope by query param; members scope by this header. Sending it for
         * both would reintroduce that bug.
         */
        const authUser = useAuthStore.getState().user;
        const isPlatformStaff = Boolean(authUser?.is_staff || authUser?.is_superuser);
        const actingTenantId = useSelectedTenantStore.getState().selectedTenantId;
        if (actingTenantId && !isPlatformStaff) {
            headers['X-Tenant-Id'] = actingTenantId;
        }

        return headers;
    }

    // Request method with 401 retry logic
    async request<T>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
        const url = `${this.baseUrl}${endpoint}`;
        const skipAuth = config.skipAuth || false;
        const isRetry = config._retry || false;

        // EAA-1762: renew a spent token BEFORE spending a request on it. Password
        // mode has no silent renew, so every request from a tab idle longer than
        // the 60-minute access-token life used to 401 first and depend on the
        // recovery path below — which meant one flaky refresh turned into a
        // visible failure on the first screen after waking up. Skipped for
        // skipAuth endpoints (login/refresh/logout), so this cannot recurse.
        if (!skipAuth && !isRetry && !getEmbedApiToken() && !getEmbedBearerToken()) {
            await authService.ensureFreshAccessToken();
        }

        let requestConfig: RequestConfig = {
            ...config,
            headers: {
                ...this.getDefaultHeaders(skipAuth),
                ...config.headers,
            },
        };

        // Apply request middlewares
        requestConfig = await this.applyRequestMiddlewares(requestConfig);

        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), config.timeout || this.timeout);

        try {
            let response = await fetch(url, {
                ...requestConfig,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            // Apply response middlewares
            response = await this.applyResponseMiddlewares(response);

            if (response.status === 401 && !isRetry && !skipAuth) {
                if (getEmbedApiToken() || getEmbedBearerToken()) {
                    clearEmbedApiToken();
                    window.dispatchEvent(new CustomEvent('auth:embed-invalid'));
                    const error: ApiError = {
                        message:
                            'Embed session expired or access denied. Reload with a valid token.',
                        status: 401,
                        code: 'EMBED_SESSION_EXPIRED',
                    };
                    throw await this.applyErrorMiddlewares(error);
                }

                const outcome = await authService.refreshAccessToken();

                if (outcome.status === 'refreshed') {
                    return this.request<T>(endpoint, {
                        ...config,
                        _retry: true,
                        headers: {
                            ...config.headers,
                            Authorization: `Bearer ${outcome.token}`,
                        },
                    });
                }

                // EAA-1762: an unreachable refresh endpoint is NOT an ended
                // session. This used to fall into the branch below and log the
                // user out, discarding a refresh token still good for days — the
                // reported "session lost after idle", which reproduces on any
                // wake-from-sleep where the network is a moment behind the tab.
                if (outcome.status === 'unavailable') {
                    const error: ApiError = {
                        message: outcome.message,
                        status: 401,
                        code: 'SESSION_REFRESH_UNAVAILABLE',
                    };
                    throw await this.applyErrorMiddlewares(error);
                }

                window.dispatchEvent(new CustomEvent('auth:logout'));
                const error: ApiError = {
                    message: 'Session expired. Please login again.',
                    status: 401,
                    code: 'SESSION_EXPIRED',
                };
                throw await this.applyErrorMiddlewares(error);
            }

            if (!response.ok) {
                let errorMessage = `HTTP error! status: ${response.status}`;
                let errorCode: number | undefined;
                let errorCodeStr: string | undefined;
                let description: string | undefined;
                let details: Record<string, string[]> | undefined;

                // Try to get error message from response body
                try {
                    const errorData = (await response.json()) as Partial<ApiEnvelope> & {
                        details?: unknown;
                    };
                    // New envelope format
                    if (errorData.message && typeof errorData.code === 'number') {
                        errorCodeStr = errorData.message;
                        errorCode = errorData.code;
                        description = errorData.description;
                        errorMessage = errorData.description || errorData.message;
                        details = normalizeEnvelopeErrorDetails(errorData.details);
                        if (details && errorCode === 2000) {
                            const firstField = Object.keys(details)[0];
                            const firstMsg = firstField ? details[firstField]?.[0] : undefined;
                            if (firstMsg) {
                                errorMessage = firstMsg;
                            }
                        }
                    } else {
                        // Legacy fallback
                        errorMessage =
                            (errorData as Record<string, string>).error ||
                            (errorData as Record<string, string>).detail ||
                            errorMessage;
                    }
                } catch {
                    // Ignore JSON parse errors
                }

                const error: ApiError = {
                    message: errorMessage,
                    description,
                    status: response.status,
                    code: errorCodeStr,
                    errorCode,
                    details,
                };
                throw await this.applyErrorMiddlewares(error);
            }
            const httpMethod = String(requestConfig.method ?? 'GET').toUpperCase();
            if (isEmbedParentMessagingActive() && !skipAuth) {
                notifyEmbedParentApiAction({
                    method: httpMethod,
                    endpoint,
                    status: response.status,
                });
            }

            // Handle empty responses (204 No Content)
            if (response.status === 204) {
                return { data: {} as T, status: response.status, ok: true };
            }

            const json = (await response.json()) as ApiEnvelope<T> | T;
            // Unwrap envelope if present
            if (
                json !== null &&
                typeof json === 'object' &&
                'success' in json &&
                'data' in json &&
                typeof (json as ApiEnvelope<T>).code === 'number'
            ) {
                return { data: (json as ApiEnvelope<T>).data, status: response.status, ok: true };
            }
            // Legacy: raw response without envelope
            return { data: json as T, status: response.status, ok: true };
        } catch (err) {
            clearTimeout(timeoutId);

            if (err instanceof Error && err.name === 'AbortError') {
                const error: ApiError = { message: 'Request timeout', code: 'TIMEOUT' };
                throw await this.applyErrorMiddlewares(error);
            }

            if ((err as ApiError).message) {
                throw err;
            }

            const error: ApiError = {
                message: err instanceof Error ? err.message : 'Unknown error',
            };
            throw await this.applyErrorMiddlewares(error);
        }
    }

    // Convenience methods
    async get<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { ...config, method: 'GET' });
    }

    async post<T>(
        endpoint: string,
        data?: unknown,
        config?: RequestConfig
    ): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            ...config,
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async put<T>(
        endpoint: string,
        data?: unknown,
        config?: RequestConfig
    ): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            ...config,
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async patch<T>(
        endpoint: string,
        data?: unknown,
        config?: RequestConfig
    ): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            ...config,
            method: 'PATCH',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async delete<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { ...config, method: 'DELETE' });
    }

    // Stream request (for SSE/streaming responses)
    async stream(endpoint: string, data?: unknown, config?: RequestConfig): Promise<Response> {
        const url = `${this.baseUrl}${endpoint}`;
        const skipAuth = config?.skipAuth || false;

        let requestConfig: RequestConfig = {
            method: 'POST',
            ...config,
            headers: {
                ...this.getDefaultHeaders(skipAuth),
                ...config?.headers,
            },
            body: data ? JSON.stringify(data) : undefined,
        };

        requestConfig = await this.applyRequestMiddlewares(requestConfig);

        const response = await fetch(url, requestConfig);

        if (!response.ok) {
            const error: ApiError = {
                message: `HTTP error! status: ${response.status}`,
                status: response.status,
            };
            throw await this.applyErrorMiddlewares(error);
        }

        const streamMethod = String(requestConfig.method ?? 'POST').toUpperCase();
        if (isEmbedParentMessagingActive() && !skipAuth) {
            notifyEmbedParentApiAction({
                method: streamMethod,
                endpoint,
                status: response.status,
            });
        }

        return response;
    }
}

// Create and configure the API client instance
// Use the Django backend URL
const API_BASE_URL = import.meta.env.VITE_AI_API_URL;

export const apiClient = new ApiClient({
    baseUrl: API_BASE_URL,
    timeout: 30000,
});

// EAA-1988: ACH holds a cơ sở offline while its slug is being renamed, refusing
// every request with 409 ERROR_TENANT_SLUG_RENAME_IN_PROGRESS. Handled here rather
// than per screen because the refusal can land on ANY request — a screen-level
// handler would mean every screen reimplementing it, and missing one shows a bare
// error with no explanation. The refusal body carries the job and phase, so the
// banner can say what is happening instead of just that something failed.
apiClient.useErrorMiddleware((error) => {
    if (error.code !== TENANT_RENAME_ERROR_CODE) return error;

    const details = (error.details ?? {}) as unknown as Record<string, unknown>;
    useTenantRenameStore.getState().startBlocking(parseTenantRenameInfo(details));
    return error;
});

// The lock is cleared ONLY by the progress poll in TenantRenameNotice, never by
// an arbitrary successful response.
//
// "Any 200 means the tenant is reachable again" was the original rule and it is
// wrong, because a lockdown is not all-or-nothing: `/tenants/me` is exempt by
// design, `/me`, `/effective/` and `/screen-rules/` are not tenant-scoped, and
// platform staff are exempt from the refusal entirely. So during a real rename a
// burst of 200s arrives alongside the 409s — and each one tore the overlay down
// microseconds after a refusal put it up. The result on the product was a lock
// that never visibly appeared at all, which is how this was found: a 409 in the
// network panel and an ordinary-looking screen.
//
// Nothing replaces it here. The endpoint that can actually answer "is the rename
// over" is the one the overlay polls, and inferring it from unrelated traffic is
// what produced the flicker.

// Add logging middleware in development
if (import.meta.env.DEV) {
    apiClient.useRequestMiddleware((config) => {
        return config;
    });

    apiClient.useResponseMiddleware((response) => {
        return response;
    });

    apiClient.useErrorMiddleware((error) => {
        return error;
    });
}

export default apiClient;
